import { Chat, DomComponent, DomComponentFactory, WithMfeContext } from '@cocounsel/harmonia';
import type { MenuItemProps } from '@mui/material/MenuItem';

export interface CreateChatProps {
  chat?: Chat.Instance;
  chatFolderIdentifier?: string;
  fileCollectionId?: string;
  isReadOnly: boolean;
  setShowResults: (showResults: boolean) => void;
  showResults: boolean;
}

export interface FileManagementOptions {
  chatIdentifier: string;
  chatFolderIdentifier?: string;
  fileCollectionId: string;
  isMatterArchived: boolean;
}

interface FileManagementProvider {
  chatIdentifier?: string;
  toggleFileManagement: () => void;
  isChatFiles: boolean;
}

interface ChatTableProvider {
  chatIdentifier?: string;
  setFileManagement: () => void;
  setResultsView: () => void;
}

interface HeaderActions {
  /** An optional factory function to render a button to display the File Management panel. */
  createFilePanel?: DomComponentFactory<FileManagementProvider>;
  /** An optional factory function to render a button to switch between messages and results in chat. */
  showChatTable?: DomComponentFactory<ChatTableProvider>;
}

/** Possible routing strategy values */
export type RoutingStrategy = 'browser' | 'memory' | 'persistentMemory';

/**
 * The props for the Matters microfrontend app component.
 * These are used for configuring the MFE component.
 *
 * @interface MattersBaseProps
 * @property {(chatIdentifier: string, chatFolderIdentifier?: string) => DomComponent} createChatComponent - Function to create a chat component for a given chat identifier.
 * @property {(options: FileManagementOptions) => DomComponent} createFileManagementComponent - Function to create a file management component.
 * @property {() => DomComponent} [createFooter] - Optional function to create a footer component.
 */
export interface ChatSidebarBaseProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (newValue: boolean) => void;
  /** Base route came from container app */
  baseRoute: string;
  /** Routing strategy used for navigate */
  routingStrategy: RoutingStrategy;
  /** Optional routing prop for changing internal routing state. */
  pathnameRouting?: string;
  /** Wheter sidebar is branded or not */
  isBranded?: boolean;
  /** Wheter to show the three dots icon that shows the available actions for chats and matters (true by default) */
  showChatAndFolderActionsMenu?: boolean;
  /** Optional parent width for responsive behavior */
  parentWidth?: number;
}

export interface LegacyMattersBaseProps {
  /** Base route came from container app */
  baseRoute: string;
  /** Routing strategy used for navigate */
  routingStrategy: RoutingStrategy;
  /** A function to render actions within the header of the main content area */
  headerActions?: HeaderActions;
  /** A property to describe if the sidebar is open */
  isOpen: boolean;
  /** The skill title to be displayed, if it exists */
  skillTitle?: string | null;
  /** A property to describe if a skills results are open */
  showSkillResult?: boolean;
  /** A function to close the sidebar */
  closeSidebar: () => void;
  /** A function to open the sidebar */
  openSidebar: () => void;
  /** A function the Matter component will call when a chat component needs to be rendered. */
  createChatComponent: (props: CreateChatProps) => DomComponent /*React.ReactNode*/;
  /** A function the Matter component will call when a file management component needs to be rendered. */
  createFileManagementComponent: (options: FileManagementOptions) => DomComponent /*React.ReactNode*/;
  /** A function to close a skills result window */
  setShowSkillResult?: () => void;
}

export type FetchResultType = {
  onDone?: () => void;
  onFail?: (error: unknown) => void;
};

export type MattersProps = WithMfeContext<LegacyMattersBaseProps>;

export type SidebarNavigationProps = WithMfeContext<MatterSidebarNavigationProps>;

export type ChatActionsMenuProps = WithMfeContext<ChatOptionsProps>;

/** Routing types and enums */
export const enum RouteItemsKeysEnum {
  MAIN_ROUTE = '',
  ADMIN_ROUTE = 'admin',
  MATTER_ROUTE = 'matter',
  FOLDER_ROUTE = 'folder',
  CHAT_ROUTE = 'chat',
  NEW_CHAT = 'new-chat',
  ARCHIVED_MATTERS = 'archived-matters',
  MATTER_SETTINGS = 'matter-settings',
  MATTER_FILES = 'matter-files',
  CHAT_FILES = 'chat-files',
  SKILLS = 'skills',
  RESULTS = 'results',
}

export const enum RouteTypeEnum {
  FIXED = 'FIXED',
  DYNAMIC = 'DYNAMIC',
}

export type ResolveParams<T> = {
  urlParam?: string;
  stores: T;
};

export type RouteNavigateProps = {
  to?: string;
  base?: string[];
  as: string;
};

export type RoutingParamType = {
  newBaseRoute: string;
  newRoutingStrategy: RoutingStrategy;
  importedRouteItems: RouteItemType<unknown>[];
};

export type RouteItemType<T> = {
  type: RouteTypeEnum;
  key: string;
  label?: string;
  icon?: string;
  currentValue?: string;
  omitRoutes?: string[];
  children?: RouteItemType<T>[];
  resolveLabel?: (resolveParams: ResolveParams<T>) => string;
  onLoaded?: (resolveParams: ResolveParams<T>) => Promise<void>;
  onUnloaded?: (resolveParams: ResolveParams<T>) => void;
};

export type ChatSidebarProps = WithMfeContext<ChatSidebarBaseProps>;

/**
 * Enumeration of available sorting options for lists.
 */
export type SortOptions = 'by_name' | 'by_date' | 'by_type';

/**
 * Defines the structure of a tree item.
 * @interface
 * @property {string} name - The name of the tree item.
 * @property {string} treeItemId - The unique identifier of the tree item.
 * @property {ItemType} type - The type of the item, distinguishing between chat and folder.
 * @property {Date} createdAt - The creation date of the tree item.
 * @property {Date} updatedAt - The update date of the tree item.
 * @property {string} [parentId] - Optional. The ID of the parent folder, if any.
 */
interface TreeBase {
  name: string;
  treeItemId: string;
  createdAt: Date;
  updatedAt: Date;
  fileCollectionId: string;
  parentId?: string;
  chat_mode?: Chat.Mode;
  resultCount?: number;
}

export interface ChatItem extends TreeBase {
  type: 'chat';
}

/**
 * Extends TreeItem to define the structure of a folder item, adding folder-specific properties.
 * @interface
 * @property {string} [matterId] - Optional. A external matterId for the folder.
 * @property {string} [description] - Optional. A description of the folder's contents or purpose.
 * @property {string} [isArchived] - Optional. A boolean for if the folders is archived.
 * @property {number[]} [dataClassifications] - Optional. Numeric codes of the sensitive-data classification types applied to the matter.
 */
export interface FolderItem extends TreeBase {
  description?: string;
  isArchived?: boolean;
  matterId?: string;
  type: 'folder';
  clientId?: string;
  dataClassifications?: number[];
}

/**
 * A sensitive-data classification type from the Government classification lookup.
 * @interface
 * @property {number} code - Stable numeric code identifying the classification type.
 * @property {string} label - Human-readable name of the classification type.
 * @property {string} [description] - Optional. Explanation of when the classification applies.
 */
export interface DataClassificationType {
  code: number;
  label: string;
  description?: string;
}

export type TreeItem = ChatItem | FolderItem;

/**
 * One page of the paginated chat/folder list (mirror of the server's PaginatedChatAndFolderList).
 * `nextChatOffset`/`nextFolderOffset` form the cursor for the next page; chats and folders are paginated
 * independently on the backend. `total` is the combined count for the current filter/search.
 */
export interface PaginatedChatList {
  items: TreeItem[];
  total: number;
  nextChatOffset: number;
  nextFolderOffset: number;
  hasMore: boolean;
}

/**
 * Defines the type of items, which can be either a folder or a chat.
 */
export type ItemType = TreeItem['type'];

export type SearchScenario = 'all' | 'archived' | string;

interface MatterSidebarNavigationProps {
  closeSidebar: () => void;
  openSidebar: () => void;
  className?: string;
  navigateBack?: () => void;
  /** Wheter sidebar header is branded or not */
  isBranded?: boolean;
  isOverlay?: boolean;
}

type MenuMode = 'active' | 'archived' | 'top-level';
export interface MenuAction extends MenuItemProps {
  label: string;
  icon?: string;
  mode?: MenuMode[];
  testId: string;
}

type MenuDirection = 'left' | 'right';

export interface ChatOptionsProps {
  direction?: MenuDirection;
  item: TreeItem;
  containerId: string;
  additionalOptions?: MenuAction[];
  isExpandedView?: boolean;
  icon?: string;
}

export enum OLYMPUS_ROUTES {
  WORK = '/work',
}
